import { useEffect, useState } from 'react';
import {
  shouldRequireOperationGoogleField,
  useGoogleVerifyConfig,
  validateGoogleCodeInput,
} from '../../hooks/useGoogleVerifyConfig';

const DEFAULT_PASSWORD = '123456';

const EMPTY_CREATE_FORM = {
  username: '',
  password: DEFAULT_PASSWORD,
  confirmPassword: DEFAULT_PASSWORD,
  realName: '',
  mobilePhone: '',
  roleId: '',
  enableIpValidation: false,
};

export default function AdminStaffEditModal({
  open,
  row,
  roles,
  rolesLoading,
  onClose,
  onSubmit,
  submitting,
}) {
  const isEdit = Boolean(row?.id);
  const { config } = useGoogleVerifyConfig();
  const requireGoogleCode = isEdit && shouldRequireOperationGoogleField(config);

  const [form, setForm] = useState(EMPTY_CREATE_FORM);
  const [googleCode, setGoogleCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }
    if (isEdit) {
      setForm({
        username: row?.username || '',
        password: '',
        confirmPassword: '',
        realName: row?.realName || '',
        mobilePhone: row?.mobilePhone || '',
        roleId: row?.roleId != null ? String(row.roleId) : '',
        enableIpValidation: Boolean(row?.enableIpValidation),
      });
    } else {
      setForm({ ...EMPTY_CREATE_FORM });
    }
    setGoogleCode('');
    setShowPassword(false);
    setError('');
  }, [open, row?.id, isEdit, row?.username, row?.realName, row?.mobilePhone, row?.roleId, row?.enableIpValidation]);

  if (!open) {
    return null;
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!isEdit && !form.username?.trim()) {
      setError('請填寫用戶名');
      return;
    }
    if (!isEdit) {
      if (!form.password?.trim()) {
        setError('請填寫密碼');
        return;
      }
      if (form.password !== form.confirmPassword) {
        setError('兩次密碼不一致');
        return;
      }
    }
    if (!form.roleId) {
      setError('請選擇角色');
      return;
    }
    if (requireGoogleCode) {
      const googleError = validateGoogleCodeInput(config, googleCode);
      if (googleError) {
        setError(googleError);
        return;
      }
    }
    setError('');

    const payload = isEdit
      ? {
          id: row.id,
          realName: form.realName?.trim() || '',
          mobilePhone: form.mobilePhone?.trim() || '',
          roleId: Number(form.roleId),
          enableIpValidation: form.enableIpValidation,
          googleCode: googleCode.trim() || undefined,
        }
      : {
          username: form.username.trim(),
          password: form.password.trim(),
          realName: form.realName?.trim() || '',
          mobilePhone: form.mobilePhone?.trim() || '',
          roleId: Number(form.roleId),
          enableIpValidation: form.enableIpValidation,
        };

    await onSubmit(payload, isEdit);
  }

  function handleReset() {
    if (isEdit) {
      setForm({
        username: row?.username || '',
        password: '',
        confirmPassword: '',
        realName: row?.realName || '',
        mobilePhone: row?.mobilePhone || '',
        roleId: row?.roleId != null ? String(row.roleId) : '',
        enableIpValidation: Boolean(row?.enableIpValidation),
      });
    } else {
      setForm({ ...EMPTY_CREATE_FORM });
    }
    setGoogleCode('');
    setError('');
  }

  const title = isEdit ? `修改 [${row?.username || ''}]` : '新增';

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--role-edit"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3>{title}</h3>
          <button type="button" className="admin-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal__body admin-modal__body--form">
            {isEdit ? (
              <div className="form-field">
                <label>帳號</label>
                <input type="text" value={form.username} disabled />
              </div>
            ) : (
              <>
                <div className="form-field">
                  <label htmlFor="staffUsername">
                    <span className="required-mark">*</span> 用戶名
                  </label>
                  <input
                    id="staffUsername"
                    type="text"
                    value={form.username}
                    onChange={(e) => updateField('username', e.target.value)}
                    placeholder="請輸入帳號"
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="staffPassword">
                    <span className="required-mark">*</span> 密碼
                  </label>
                  <div className="password-input-wrap">
                    <input
                      id="staffPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => updateField('password', e.target.value)}
                    />
                    <button
                      type="button"
                      className="password-input-wrap__toggle"
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? '隱藏' : '顯示'}
                    </button>
                  </div>
                </div>
                <div className="form-field">
                  <label htmlFor="staffConfirmPassword">
                    <span className="required-mark">*</span> 確認密碼
                  </label>
                  <input
                    id="staffConfirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={(e) => updateField('confirmPassword', e.target.value)}
                  />
                </div>
                <p className="page-desc form-note">（備註：預設密碼為 {DEFAULT_PASSWORD}）</p>
              </>
            )}

            <div className="form-field">
              <label htmlFor="staffRealName">姓名</label>
              <input
                id="staffRealName"
                type="text"
                value={form.realName}
                onChange={(e) => updateField('realName', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="staffMobile">手機號碼</label>
              <input
                id="staffMobile"
                type="text"
                value={form.mobilePhone}
                onChange={(e) => updateField('mobilePhone', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="staffRoleId">
                <span className="required-mark">*</span> 分配角色
              </label>
              <select
                id="staffRoleId"
                value={form.roleId}
                onChange={(e) => updateField('roleId', e.target.value)}
                disabled={rolesLoading}
              >
                <option value="">請選擇</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.role}
                  </option>
                ))}
              </select>
            </div>
            <fieldset className="form-field">
              <legend>是否啟用 IP 白名單登入驗證</legend>
              <label className="radio-label">
                <input
                  type="radio"
                  name="enableIpValidation"
                  checked={form.enableIpValidation === true}
                  onChange={() => updateField('enableIpValidation', true)}
                />
                是
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="enableIpValidation"
                  checked={form.enableIpValidation === false}
                  onChange={() => updateField('enableIpValidation', false)}
                />
                否
              </label>
            </fieldset>

            {requireGoogleCode && (
              <div className="form-field">
                <label htmlFor="staffGoogleCode">
                  <span className="required-mark">*</span> 谷歌驗證碼
                </label>
                <input
                  id="staffGoogleCode"
                  type="text"
                  inputMode="numeric"
                  value={googleCode}
                  onChange={(e) => setGoogleCode(e.target.value)}
                  placeholder="請輸入 6 位驗證碼"
                />
              </div>
            )}

            {error && <div className="alert alert-error">{error}</div>}
          </div>
          <div className="admin-modal__footer admin-modal__footer--center">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? '提交中…' : '提交'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={submitting}>
              重置
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              關閉
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
